import dayjs from 'dayjs'

const state = () => ({
  user: JSON.parse(localStorage.getItem('akern_customer_user')) || null,
  token: localStorage.getItem('akern_customer_token') || null,
  license: null,
  device: null,
  formulaVersion: null,
  softwareVersion: process.env.appVersion || '0',
  settings: null,
  versionUpdates: JSON.parse(
    localStorage.getItem('akern_customer_versions')
  ) || {
    web: { version: null, date: null },
    standalone: { version: null, date: null },
    shared: { version: null, date: null },
  },
  hideHL7: localStorage.getItem('hideHL7') || false,
})
const getters = {
  user: (state) => state.user,
  token: (state) => state.token,
  license: (state) => state.license,
  device: (state) => state.device,
  formulaVersion: (state) => state.formulaVersion,
  softwareVersion: (state) => state.softwareVersion,
  settings: (state) => state.settings,
  isAuthenticated: (state) => {
    if (state.user) {
      if (state.user.authorizedUntil) {
        const currentDate = dayjs().utc()
        const authorizedUntil = dayjs(state.user.authorizedUntil)
        const daysToLogout = authorizedUntil.diff(currentDate, 'day')
        return daysToLogout > 0
      } else {
        return true
      }
    } else {
      return false
    }
  },
  permissions: (state) => {
    if (state.user && state.user.permissions) {
      return state.user.permissions
    } else {
      return ['*']
    }
  },
  versionUpdatedToday: (state) => {
    const appType = process.env.appType
    const appVersion = process.env.appVersion

    if (state.versionUpdates[appType].version !== appVersion) return false

    const lastUpdated = state.versionUpdates[appType].date

    if (!lastUpdated) return false

    return dayjs(lastUpdated).isSame(dayjs(), 'day')
  },
  hideHL7: (state) => state.hideHL7,
  isPasswordChangeRequired: (state) => {
    if (!state.user) return false
    const at = state.user.passwordChangedAt
    if (!at) return true
    return dayjs().diff(dayjs(at), 'month') >= 6
  },
  isImpersonation: (state) => !!(state.user && state.user.isImpersonation),
  impersonationExpiresAt: (state) =>
    state.user && state.user.impersonationExpiresAt
      ? state.user.impersonationExpiresAt
      : null,
}
const mutations = {
  receiveUser(state, user) {
    const merged = { ...user }
    if (state.user && state.user.isImpersonation) {
      merged.isImpersonation = state.user.isImpersonation
      merged.impersonationExpiresAt = state.user.impersonationExpiresAt
    }
    // Locale is owned client-side (set via `updateLocale` from the settings
    // page and persisted separately). A refetched account payload carries a
    // stale locale, so preserve the current selection instead of letting
    // account/get clobber it back to the previous language.
    if (state.user && state.user.locale) {
      merged.locale = state.user.locale
    }
    state.user = merged
    localStorage.setItem('akern_customer_user', JSON.stringify(merged))
  },
  receiveToken(state, token) {
    state.token = token
    localStorage.setItem('akern_customer_token', token)
  },
  logout(state) {
    state.token = null
    state.user = null
    localStorage.removeItem('akern_customer_token')
    localStorage.removeItem('akern_customer_user')
    localStorage.removeItem('akern_customer_versions')
    localStorage.removeItem('hideHL7')
  },
  receiveLicense(state, license) {
    state.license = license
  },
  receiveDevice(state, device) {
    state.device = device
  },
  receiveFormulaVersion(state, formulaVersion) {
    state.formulaVersion = formulaVersion
  },
  receiveSoftwareVersion(state, softwareVersion) {
    state.softwareVersion = softwareVersion
  },
  receiveSettings(state, settings) {
    state.settings = settings
  },
  updateLocale(state, locale) {
    const user = JSON.parse(localStorage.getItem('akern_customer_user'))
    user.locale = locale
    localStorage.setItem('akern_customer_user', JSON.stringify(user))
    state.user = user
    state.settings.locale = locale
  },
  updateHideHL7(state, hideHL7) {
    state.hideHL7 = hideHL7
    localStorage.setItem('hideHL7', hideHL7)
  },
  setGoogleAnalyticsUserId(state) {
    if (state.user && state.user.id && window.dataLayer) {
      window.dataLayer.push({
        event: 'user_id_set',
        user_id: state.user.id,
        application_type:
          process.env.platform === 'web' ? 'Web' : 'Desktop App',
        app_version: process.env.appVersion,
      })
    }
  },
  setVersionUpdates(state) {
    state.versionUpdates[process.env.appType].version = process.env.appVersion
    state.versionUpdates[process.env.appType].date = dayjs().utc()
    localStorage.setItem(
      'akern_customer_versions',
      JSON.stringify(state.versionUpdates)
    )
  },
}
let _pendingFetchUser = null
let _userInvalidated = false

const actions = {
  login({ commit, rootGetters, dispatch }, data) {
    if (
      window.$nuxt.$platform === 'electron' &&
      rootGetters['utils/isStandalone']
    ) {
      this.$axios.post(
        `${rootGetters['network/localAPI']}/reset-download-date`,
        {}
      )
    }
    return this.$axios
      .post('/akern-platform-backend/v1/customer/account/sign-in', data)
      .then((response) => {
        commit('receiveToken', response.data.user.accessToken)
        commit('receiveUser', response.data.user)
        dispatch('getSettings')
      })
      .catch((error) => {
        console.log(error)
        throw error
      })
  },
  forgot({ commit }, email) {
    return this.$axios.post(
      '/akern-platform-backend/v1/common/password/forgot',
      {
        email,
      }
    )
  },
  reset({ commit }, data) {
    return this.$axios.post(
      '/akern-platform-backend/v1/common/password/reset',
      data
    )
  },
  logout({ commit }) {
    commit('logout')
    this.$router.push('/login')
  },
  pretendLogout({ state, commit }) {
    const request = state.token
      ? this.$axios
          .post('/akern-platform-backend/v1/customer/users/pretend-logout', {
            accessToken: state.token,
          })
          .catch((error) => {
            console.log(error)
          })
      : Promise.resolve()
    return request.then(() => {
      commit('logout')
      this.$router.push('/login')
    })
  },
  getSettings({ state, commit, rootGetters }) {
    const url = rootGetters['network/isLocalBackend']
      ? '/bodygram-backend/v1/settings/general/get'
      : '/akern-platform-backend/v1/customer/settings/get'
    return this.$axios
      .post(url, {
        accessToken: state.token,
      })
      .then((response) => {
        const settings = { ...response.data.settings }
        if (rootGetters['network/onLine'] && !settings.contactEmail) {
          settings.contactEmail = JSON.parse(JSON.stringify(state.user.email))
        }
        settings.enableNaeKe = !!settings.enableNaeKe
        settings.enableBcm = !!settings.enableBcm
        settings.enableMm = !!settings.enableMm
        settings.hideGlossary = !!settings.hideGlossary
        settings.showIdealWeight = !!settings.showIdealWeight
        if (rootGetters['utils/isShared']) {
          settings.isMandatoryPatientCode = !!settings.isMandatoryPatientCode
        }
        commit('receiveSettings', {
          ...settings,
        })
        if (settings.locale) commit('updateLocale', settings.locale)
      })
      .catch((error) => {
        console.log(error)
      })
  },
  fetchUser({ state, commit }) {
    if (!state.token || !state.user?.id) return Promise.resolve()
    if (_pendingFetchUser) return _pendingFetchUser
    if (!_userInvalidated && state.user) return Promise.resolve()
    _userInvalidated = false
    _pendingFetchUser = this.$axios
      .post('/akern-platform-backend/v1/customer/account/get', {
        accessToken: state.token,
        id: state.user.id,
      })
      .then((response) => {
        _pendingFetchUser = null
        // permissions and superLog come only from sign-in: Electron (HBO)
        // account/get omits them, and losing permissions would fall back
        // to ['*'] in the permissions getter
        const user = { ...response.data.user }
        if (!user.permissions && state.user.permissions) {
          user.permissions = state.user.permissions
        }
        if (user.superLog === undefined && state.user.superLog !== undefined) {
          user.superLog = state.user.superLog
        }
        commit('receiveUser', user)
      })
      .catch((error) => {
        _pendingFetchUser = null
        console.log(error)
      })
    return _pendingFetchUser
  },
  invalidateUser() {
    _userInvalidated = true
  },
  getLicense({ state, commit }) {
    return this.$axios
      .post('/bodygram-backend/v1/settings/licenses/get', {
        accessToken: state.token,
      })
      .then((response) => {
        commit('receiveLicense', response.data.license)
        commit('receiveDevice', response.data.device)
        commit('receiveFormulaVersion', response.data.formulaVersion)
        // commit('receiveSoftwareVersion', response.data.softwareVersion)
      })
      .catch((error) => console.error(error))
  },
}
export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true,
}
